import { TenantScope } from '@logto/schemas';
import { useContext, useMemo } from 'react';
import useSWR from 'swr';

import { useAuthedCloudApi } from '@/cloud/hooks/use-cloud-api';
import { isCloud, isMultiTenant } from '@/consts/env';
import { TenantsContext } from '@/contexts/TenantsProvider';

import { type RequestError } from './use-api';
import useCurrentUser from './use-current-user';

// The /scopes endpoint exists in Logto Cloud and (since self-hosted Members support landed)
// in self-hosted multi-tenant. Single-tenant OSS doesn't have a per-tenant scope concept
// at all, so we treat the operator as a full admin there.
const hasTenantScopeApi = isCloud || isMultiTenant;

const fullAccess = {
  canInviteMember: true,
  canRemoveMember: true,
  canUpdateMemberRole: true,
  canManageTenant: true,
} as const;

const useCurrentTenantScopes = () => {
  const { currentTenantId } = useContext(TenantsContext);
  const cloudApi = useAuthedCloudApi();
  const { user } = useCurrentUser();
  const userId = user?.id ?? '';

  const {
    data: scopes,
    isLoading,
    mutate,
  } = useSWR<string[], RequestError>(
    hasTenantScopeApi && userId && `api/tenants/${currentTenantId}/members/${userId}/scopes`,
    async () => {
      const scopes = await cloudApi.get('/api/tenants/:tenantId/members/:userId/scopes', {
        params: { tenantId: currentTenantId, userId },
      });
      return scopes.map(({ name }) => name);
    }
  );

  const access = useMemo(
    () =>
      hasTenantScopeApi
        ? {
            canInviteMember: Boolean(scopes?.includes(TenantScope.InviteMember)),
            canRemoveMember: Boolean(scopes?.includes(TenantScope.RemoveMember)),
            canUpdateMemberRole: Boolean(scopes?.includes(TenantScope.UpdateMemberRole)),
            canManageTenant: Boolean(scopes?.includes(TenantScope.ManageTenant)),
          }
        : { ...fullAccess },
    [scopes]
  );

  return useMemo(
    () =>
      hasTenantScopeApi
        ? { isLoading, scopes, access, mutate }
        : { isLoading: false, scopes: undefined, access, mutate },
    [isLoading, scopes, access, mutate]
  );
};

export default useCurrentTenantScopes;
